import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './context/AuthContext';
import ErrorBoundary from './components/ErrorBoundary';
import Login from './pages/Login';

// Lazy pages (Login is eager — required on logout without chunk reload)
const AdminLayout = lazy(() => import('./layouts/AdminLayout'));
const AdminSettingsLayout = lazy(() =>
  import('./layouts/AdminLayout').then((m) => ({ default: m.AdminSettingsLayout }))
);

// Admin pages
const Dashboard            = lazy(() => import('./pages/admin/Dashboard'));
const Onboarding           = lazy(() => import('./pages/admin/Onboarding'));
const ProjectOnboarding    = lazy(() => import('./pages/admin/ProjectOnboarding'));
const MyTasks              = lazy(() => import('./pages/admin/MyTasks'));
const KanbanBoard          = lazy(() => import('./pages/admin/KanbanBoard'));
const Calendar             = lazy(() => import('./pages/admin/Calendar'));
const SpentTime            = lazy(() => import('./pages/admin/SpentTime'));
const PlanPage             = lazy(() => import('./pages/admin/PlanPage'));
const WorkorderPage        = lazy(() => import('./pages/admin/WorkorderPage'));
const TasksPage            = lazy(() => import('./pages/admin/TasksPage'));
const OneTimeTaskPage      = lazy(() => import('./pages/admin/OneTimeTaskPage'));

const Loader = () => (
  <div className="loading-screen">
    <div className="spinner-custom" />
    <p className="loading-screen-text">Loading kiwistron taskmanagement...</p>
  </div>
);

const ProtectedRoute = ({ children }) => {
  const { loading, token } = useAuth();
  if (loading) return <Loader />;
  if (!token) return <Navigate to="/login" replace />;
  return children;
};

const AdminOnlyRoute = ({ children }) => {
  const { loading, isAdmin } = useAuth();
  if (loading) return <Loader />;
  if (!isAdmin) return <Navigate to="/admin" replace />;
  return children;
};

function App() {
  return (
    <BrowserRouter>
        <Suspense fallback={<Loader />}>
          <Routes>
            <Route path="/login" element={<Login />} />

            {/* Admin Routes */}
            <Route path="/admin" element={<ProtectedRoute><AdminLayout /></ProtectedRoute>}>
              <Route index element={<ErrorBoundary><Dashboard /></ErrorBoundary>} />
              <Route path="tasks" element={<ErrorBoundary><MyTasks /></ErrorBoundary>} />
              <Route path="kanban" element={<ErrorBoundary><KanbanBoard /></ErrorBoundary>} />
              <Route path="calendar" element={<Calendar />} />
              <Route path="spent-time" element={<SpentTime />} />

              <Route path="plan" element={<PlanPage />} />
              <Route path="workorder" element={<WorkorderPage />} />
              <Route path="tasks-all" element={<TasksPage />} />
              <Route
                path="one-time-task"
                element={
                  <AdminOnlyRoute>
                    <OneTimeTaskPage />
                  </AdminOnlyRoute>
                }
              />
              
              {/* Admin Settings Routes with Sub-Sidebar */}
              <Route element={<AdminSettingsLayout />}>
                <Route path="onboarding" element={<Onboarding />} />
                <Route path="project-onboarding" element={<ProjectOnboarding />} />
              </Route>
            </Route>

            <Route path="/" element={<Navigate to="/login" />} />
            <Route path="*" element={<Navigate to="/" />} />

          </Routes>
        </Suspense>
    </BrowserRouter>
  );
}

export default App;
